import { useState, type FormEvent, useEffect, useRef } from 'react';
import { motion } from 'motion/react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/pages/home/components/Footer';
import { brandInfo } from '@/mocks/homeData';
import { config } from '@/config';

declare global {
  interface Window {
    turnstile?: {
      render: (container: string | HTMLElement, options: Record<string, unknown>) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
    onTurnstileLoadBook?: () => void;
  }
}

const serviceOptions = [
  'Screen Replacement',
  'Battery Replacement',
  'Charging Port Repair',
  'Motherboard / IC Repair',
  'Water Damage Recovery',
  'Software / Unlocking',
  'Home Appliance Repair',
  'TV Installation / Setup',
  'DTH Installation',
  'DTH Recharge',
  'Money Transfer',
  'Mobile / Appliance Purchase',
  'Accessories / Gadgets',
  'Other',
];

export default function BookServicePage() {
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [selectedService, setSelectedService] = useState('');
  const turnstileRef = useRef<HTMLDivElement | null>(null);
  const turnstileIdRef = useRef<string>('');
  const turnstileLoaded = useRef(false);

  // Load Turnstile script once
  useEffect(() => {
    if (!config.forms.turnstileSiteKey) return;
    if (turnstileLoaded.current) return;

    const existingScript = document.querySelector('script[src*="turnstile/v0/api.js"]');
    if (existingScript && window.turnstile) {
      turnstileLoaded.current = true;
      // Render widget if container exists
      if (turnstileRef.current) {
        try {
          turnstileIdRef.current = window.turnstile.render(turnstileRef.current, {
            sitekey: config.forms.turnstileSiteKey,
            theme: 'light',
            size: 'normal',
          });
        } catch { /* ignore */ }
      }
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=onTurnstileLoadBook';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    (window as unknown as Record<string, unknown>).onTurnstileLoadBook = () => {
      turnstileLoaded.current = true;
      if (turnstileRef.current) {
        try {
          turnstileIdRef.current = window.turnstile!.render(turnstileRef.current, {
            sitekey: config.forms.turnstileSiteKey,
            theme: 'light',
            size: 'normal',
          });
        } catch { /* ignore */ }
      }
    };
  }, []);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setStatus('success');
      return;
    }

    formData.delete('website_alt');
    setStatus('idle');
    setErrorMsg('');

    try {
      const response = await fetch(config.forms.bookServiceUrl, {
        method: 'POST',
        body: new URLSearchParams(formData as unknown as Record<string, string>),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });

      const responseText = await response.text();
      let parsed: { success?: boolean; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.success) {
        setStatus('success');
        form.reset();
        setSelectedService('');
        if (turnstileIdRef.current && window.turnstile) {
          try { window.turnstile.reset(turnstileIdRef.current); } catch { /* ignore */ }
        }
      } else {
        setErrorMsg(parsed?.message || 'Submission failed. Please try again.');
        setStatus('error');
        if (turnstileIdRef.current && window.turnstile) {
          try { window.turnstile.reset(turnstileIdRef.current); } catch { /* ignore */ }
        }
      }
    } catch {
      setErrorMsg('Network error. Please check your connection and try again.');
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[400px] md:min-h-[480px] flex items-center justify-center overflow-hidden pt-16">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Professional%20mobile%20phone%20repair%20service%20booking%20concept%2C%20clean%20modern%20workspace%20with%20appointment%20calendar%20smartphone%20and%20tools%20on%20desk%2C%20warm%20ambient%20lighting%2C%20minimal%20modern%20office%20interior%2C%20soft%20natural%20light%2C%20photorealistic%20editorial%20style%2C%20warm%20neutral%20tones&width=1600&height=900&seq=booking-hero-01&orientation=landscape"
            alt="Book a Service"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/70 via-foreground-950/60 to-foreground-950/80"></div>
        </div>

        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 text-center pt-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
          >
            <span className="inline-block bg-accent-500/20 text-accent-300 px-4 py-1.5 rounded-full text-xs md:text-sm font-semibold mb-4">
              Quick & Easy Booking
            </span>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-4">
              Book a Service
            </h1>
            <p className="text-base md:text-lg text-background-200 max-w-2xl mx-auto">
              Request a repair, installation, or any service from Ashok Telecom. We will confirm your booking and get back to you within 30 minutes.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="py-12 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 md:gap-10 max-w-6xl mx-auto">

            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              className="lg:col-span-3"
            >
              <form
                onSubmit={handleSubmit}
                className="bg-background-100 border border-background-200/70 rounded-xl p-5 md:p-8 hover:border-primary-300 transition-colors duration-300"
              >
                <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-6">
                  Service Request Form
                </h2>

                <div className="honeypot-field">
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                <div className="space-y-5">
                  {/* Service Type */}
                  <div>
                    <label htmlFor="service_type" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      Service Type <span className="text-primary-500">*</span>
                    </label>
                    <select
                      id="service_type"
                      name="service_type"
                      required
                      value={selectedService}
                      onChange={(e) => setSelectedService(e.target.value)}
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors cursor-pointer"
                    >
                      <option value="">Select a service</option>
                      {serviceOptions.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>

                  {/* Device / Brand */}
                  <div>
                    <label htmlFor="device_brand" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      Device / Brand
                    </label>
                    <input
                      type="text"
                      id="device_brand"
                      name="device_brand"
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="e.g., Samsung Galaxy S23, LG Refrigerator"
                    />
                  </div>

                  {/* Problem Description */}
                  <div>
                    <label htmlFor="description" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      Describe the Issue <span className="text-primary-500">*</span>
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                      placeholder="Tell us what is wrong with your device or what service you need..."
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">Maximum 500 characters</p>
                  </div>

                  {/* Name + Phone */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        Your Name <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="Full name"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        Phone Number <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="+91 9XXXX XXXXX"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="your@email.com"
                    />
                  </div>

                  {/* Preferred Date + Time */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="preferred_date" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        Preferred Date
                      </label>
                      <input
                        type="date"
                        id="preferred_date"
                        name="preferred_date"
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors cursor-pointer"
                      />
                    </div>
                    <div>
                      <label htmlFor="preferred_time" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        Preferred Time
                      </label>
                      <select
                        id="preferred_time"
                        name="preferred_time"
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors cursor-pointer"
                      >
                        <option value="">Any time</option>
                        <option value="morning">Morning (9 AM – 12 PM)</option>
                        <option value="afternoon">Afternoon (12 PM – 4 PM)</option>
                        <option value="evening">Evening (4 PM – 9 PM)</option>
                      </select>
                    </div>
                  </div>

                  {/* Address */}
                  <div>
                    <label htmlFor="address" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      Your Address / Location
                    </label>
                    <textarea
                      id="address"
                      name="address"
                      rows={2}
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                      placeholder="Where should we visit or where can you bring the device?"
                    ></textarea>
                  </div>

                  {config.forms.turnstileSiteKey && (
                    <div ref={turnstileRef} className="flex justify-center"></div>
                  )}

                  {/* Submit */}
                  <button
                    type="submit"
                    className="w-full bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold py-3 rounded-lg text-sm transition-all whitespace-nowrap cursor-pointer hover:scale-[1.01] active:scale-[0.99]"
                  >
                    {status === 'idle' ? 'Submit Booking Request' : status === 'success' ? 'Booking Sent!' : 'Try Again'}
                  </button>

                  {status === 'success' && (
                    <motion.p
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-sm text-accent-600 font-medium text-center"
                    >
                      Thank you! We will call you within 30 minutes to confirm your booking.
                    </motion.p>
                  )}

                  {status === 'error' && (
                    <motion.p
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-sm text-primary-600 font-medium text-center"
                    >
                      {errorMsg || 'Something went wrong. Please try again.'}
                    </motion.p>
                  )}
                </div>
              </form>
            </motion.div>

            {/* Info Sidebar */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6, ease: 'easeOut', delay: 0.1 }}
              className="lg:col-span-2 space-y-5"
            >
              {/* Store Info Card */}
              <div className="bg-background-100 border border-background-200/70 rounded-xl p-5 md:p-6">
                <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-4">
                  Store Information
                </h3>
                <div className="space-y-3.5">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-primary-100 rounded-lg shrink-0">
                      <i className="ri-map-pin-fill text-primary-600 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">Address</p>
                      <p className="text-sm text-foreground-600">{brandInfo.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-accent-100 rounded-lg shrink-0">
                      <i className="ri-time-fill text-accent-600 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">Business Hours</p>
                      <p className="text-sm text-foreground-600">{brandInfo.hours}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-secondary-100 rounded-lg shrink-0">
                      <i className="ri-phone-fill text-secondary-600 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">Phone</p>
                      <a href={`tel:${brandInfo.phone}`} className="text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer">
                        {brandInfo.phone}
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* WhatsApp Quick Book */}
              <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-5 md:p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-emerald-500 rounded-full shrink-0">
                    <i className="ri-whatsapp-line text-background-50 text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-heading text-base font-semibold text-foreground-950">
                      Book on WhatsApp
                    </h3>
                    <p className="text-xs text-foreground-500">Fastest way to reach us</p>
                  </div>
                </div>
                <p className="text-sm text-foreground-600 mb-4">
                  Send us your device issue on WhatsApp and we will reply with a time slot and quote.
                </p>
                <a
                  href={`https://wa.me/${brandInfo.phone.replace('+', '')}?text=Hi%20Ashok%20Telecom%2C%20I%20would%20like%20to%20book%20a%20service.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-chat-3-line text-xs"></i>
                  </span>
                  Message on WhatsApp
                </a>
              </div>

              {/* Why Book With Us */}
              <div className="bg-background-100 border border-background-200/70 rounded-xl p-5 md:p-6">
                <h3 className="font-heading text-base font-semibold text-foreground-950 mb-4">
                  Why Book With Us
                </h3>
                <ul className="space-y-3">
                  {[
                    '30-minute response time',
                    'Genuine parts & warranty',
                    'Home visit available for appliances',
                    'Transparent pricing — no hidden charges',
                    'Same-day service for most repairs',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm text-foreground-700">
                      <span className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                        <i className="ri-check-line text-accent-500 text-sm"></i>
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Call Direct */}
              <a
                href={`tel:${brandInfo.phone}`}
                className="block bg-primary-500 hover:bg-primary-600 text-background-50 text-center py-3.5 rounded-xl text-sm font-semibold transition-all cursor-pointer"
              >
                <span className="inline-flex items-center gap-2">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-phone-fill text-xs"></i>
                  </span>
                  Call Directly: {brandInfo.phone}
                </span>
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}